import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import LoginPage from "../pages/auth/LoginPage";
import { ForgotPasswordPage, ResetPasswordPage } from '../pages/auth/PasswordResetPage';
import Header from '../components/Header';
import Menu from '../components/Menu';
import Dashboard from "../pages/dashboard/Dashboard";
import EditSubmittedForm from "../pages/form/EditSubmittedForm";
import LocationPage from '../pages/admin/LocationPage';
import AccessSetupPage from '../pages/admin/AccessSetupPage';
import UserCreatePage from '../pages/admin/UserCreatePage';
import EventLogsPage from '../pages/admin/EventLogsPage';
import ProfilePage from '../pages/profile/ProfilePage';
import { VehicleDamageClaimsOpenPage, VehicleDamageClaimsSubmittedPage, VehicleDamageClaimsEditPage, VehicleDamageClaimsClosedPage } from '../pages/claims/VehicleDamageClaimsPages';
import { ShuttleBusOpenPage, ShuttleBusSubmittedPage, ShuttleBusEditPage, ShuttleBusClosedPage } from '../pages/claims/ShuttleBusClaimsPages';
import { WindshieldTireOpenPage, WindshieldTireSubmittedPage, WindshieldTireClosedPage } from '../pages/claims/WindshieldTireClaimsPages';
import { getLoggedInUser, hasModulePermission } from '../utils/permissions';
import PendingClaimsReport from '../pages/reports/PendingClaimsReport';
import ClosedClaimsReport from '../pages/reports/ClosedClaimsReport';
import WeeklyClaimsPptReport from '../pages/reports/WeeklyClaimsPptReport';
import SearchRecordsPage from '../pages/records/SearchRecordsPage';
import InsightsPage from '../pages/insights/InsightsPage';
import AnalyticsPage from '../pages/analytics/AnalyticsPage';

const SHUTTLE_BUS_PERMISSION_KEYS = [
  'Shuttle Bus Claims',
  'Open Shuttle Bus Claim',
  'Submitted Shuttle Bus Claims',
];
const VEHICLE_OPEN_KEYS = ['Vehicle Damage Claims / Claims To Be Reported', 'Vehicle/Fleet Claims / Claims To Be Reported', 'Vehicle Damage Claims / Open Form', 'Vehicle/Fleet Claims / Open Form', 'Open Form', 'Open Report'];
const VEHICLE_SUBMITTED_KEYS = ['Vehicle Damage Claims / Submited Forms', 'Vehicle Damage Claims / Submitted Forms', 'Vehicle/Fleet Claims / Submited Forms', 'Vehicle/Fleet Claims / Submitted Forms', 'Submited Forms', 'Submitted Forms'];
const VEHICLE_CLOSED_KEYS = ['Vehicle Damage Claims / Closed Forms', 'Vehicle Damage Claims / Closed Form', 'Vehicle/Fleet Claims / Closed Forms', 'Vehicle/Fleet Claims / Closed Form', 'Close Form', 'Closed Form', 'Closed Forms', 'Close Report'];
const SHUTTLE_OPEN_KEYS = ['Shuttle Bus Claims / Claims To Be Reported', 'Shuttle Bus Claims / Open Form', ...SHUTTLE_BUS_PERMISSION_KEYS];
const SHUTTLE_SUBMITTED_KEYS = ['Shuttle Bus Claims / Submited Forms', 'Shuttle Bus Claims / Submitted Forms', ...SHUTTLE_BUS_PERMISSION_KEYS];
const SHUTTLE_CLOSED_KEYS = ['Shuttle Bus Claims / Closed Forms', 'Shuttle Bus Claims / Closed Form', ...SHUTTLE_BUS_PERMISSION_KEYS];

const WINDSHIELD_TIRE_PERMISSION_KEYS = [
  'Windshield/Tire Claims',
  'Windshield / Tire Claims',
  'Windshield Tire Claims',
  'Open Windshield/Tire Claim',
  'Submitted Windshield/Tire Claims',
  'Submited Windshield Tire Forms',
  'Submited Windshield/Tire Forms',
  'Closed Windshield Tire Form',
  'Closed Windshield/Tire Form',
];
const WINDSHIELD_OPEN_KEYS = ['Windshield/Tire Claims / Open Form', 'Windshield / Tire Claims / Open Form', ...WINDSHIELD_TIRE_PERMISSION_KEYS];
const WINDSHIELD_SUBMITTED_KEYS = ['Windshield/Tire Claims / Submited Forms', 'Windshield/Tire Claims / Submitted Forms', ...WINDSHIELD_TIRE_PERMISSION_KEYS];
const WINDSHIELD_CLOSED_KEYS = ['Windshield/Tire Claims / Closed Forms', 'Windshield/Tire Claims / Closed Form', ...WINDSHIELD_TIRE_PERMISSION_KEYS];
const PENDING_REPORT_KEYS = ['Reports / Pending Form', 'Reports / Pending Claims', 'Reports / Pending Claims Report', 'Pending Form', 'Pending Claims', 'Pending Claims Report'];
const CLOSED_REPORT_KEYS = ['Reports / Closed Form', 'Reports / Closed Claims', 'Reports / Closed Claims Report', 'Closed Form', 'Closed Claims', 'Closed Claims Report'];
const WEEKLY_PPT_REPORT_KEYS = ['Reports / Weekly Claims PPT', 'Reports / Weekly Claims PowerPoint', 'Reports / weekly-claims-ppt', 'Weekly Claims PPT', 'Weekly Claims PowerPoint', 'weekly-claims-ppt'];
const SEARCH_RECORDS_KEYS = ['Search Records', 'search-records'];
const INSIGHTS_KEYS = ['Insights', 'insights'];

const WINDSHIELD_TIRE_SUBMITTED_PROPS = {
  fetchEndpoint: '/get_submitted_windshield_tire_open_forms',
  historyEndpointBase: '/windshield_tire_open_form_event_logs',
  permissionKeys: WINDSHIELD_SUBMITTED_KEYS,
  title: 'Submitted Windshield / Tire Claims',
  subtitle: 'Search, review, and edit submitted windshield and tire claims.',
  noFoundMessage: 'No submitted windshield or tire claims found for the selected location.',
  accessDeniedMessage: 'You do not have permission to view windshield or tire claims.',
  historyTitlePrefix: 'Windshield / Tire Claim History',
  historySubtitle: 'Create and edit activity for this windshield or tire claim.',
  processingFetchEndpoint: '/get_windshield_tire_claim_processing_forms',
  processingHistoryEndpointBase: '/windshield_tire_claim_processing_event_logs',
  processingUpdateEndpointBase: '/update_windshield_tire_claim_processing',
  processingCloseEndpointBase: '/move_windshield_tire_processing_to_closed',
  processingMoveBackEndpointBase: '/move_windshield_tire_processing_back_to_reported',
  markViewedEndpointBase: '',
  showProcessing: true,
  reportedLockWorkflow: 'Windshield / Tire Claims Reported',
  processingLockWorkflow: 'Windshield / Tire Claims Processing',
  editFormProps: {
    updateEndpointBase: '/update_submitted_windshield_tire_open_form',
    submittedFormsPath: '/windshield-tire-claims/submitted-forms',
    permissionKeys: WINDSHIELD_SUBMITTED_KEYS,
    workflowName: 'windshield / tire claim form',
    accessDeniedMessage: 'You do not have permission to edit windshield or tire claims.',
    workflowTag: 'Windshield / Tire',
    workflowVariant: 'windshield-tire',
    promoteEndpointBase: '/promote_windshield_tire_to_claim_processing',
    promoteToProcessingOnSubmit: true,
    finalSubmitLabel: 'Claims Processing',
  },
};

function ProtectedRoute({ element, moduleNames = [], defaultWhenUnset = true }) {
  if (!moduleNames.length) {
    return element;
  }

  const accessRole = getLoggedInUser()?.accessRole || {};

  if (!hasModulePermission(moduleNames, 'View', accessRole, defaultWhenUnset)) {
    return <Navigate to="/" replace />;
  }

  return element;
}

function AppRoutes() {
  const [loginSuccess, setLoginSuccess] = useState(false)
  useEffect(() => {
    const loggedInUser = sessionStorage.getItem("user_token_ra");
    if (loggedInUser) {
      setLoginSuccess(true);
    }
  },[])
  return (
    <BrowserRouter>
      {loginSuccess?
        <>
          <Header />
          <Menu />
        </>:""
      }
      <Routes>
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        {!loginSuccess?
          <Route path="/" element={<LoginPage setLoginSuccess={setLoginSuccess} />} />
          :
          (<>
            <Route path="/" element={<Dashboard />} />
            <Route path="/location" element={<ProtectedRoute moduleNames={['Location', 'Location Setup']} element={<LocationPage />} />} />
            <Route path="/form" element={<Navigate to="/close-form" replace />} />
            <Route path="/open-form" element={<ProtectedRoute moduleNames={VEHICLE_OPEN_KEYS} element={<VehicleDamageClaimsOpenPage />} />} />
            <Route path="/close-form" element={<ProtectedRoute moduleNames={VEHICLE_CLOSED_KEYS} element={<VehicleDamageClaimsClosedPage />} />} />
            <Route path="/submitted-forms" element={<ProtectedRoute moduleNames={VEHICLE_SUBMITTED_KEYS} element={<VehicleDamageClaimsSubmittedPage />} />} />
            <Route path="/submitted-forms/:id/edit" element={<ProtectedRoute moduleNames={VEHICLE_SUBMITTED_KEYS} element={<VehicleDamageClaimsEditPage />} />} />
            <Route path="/submitted-form" element={<ProtectedRoute moduleNames={VEHICLE_SUBMITTED_KEYS} element={<VehicleDamageClaimsSubmittedPage />} />} />
            <Route path="/submited-forms" element={<ProtectedRoute moduleNames={VEHICLE_SUBMITTED_KEYS} element={<VehicleDamageClaimsSubmittedPage />} />} />
            <Route path="/submited-form" element={<ProtectedRoute moduleNames={VEHICLE_SUBMITTED_KEYS} element={<VehicleDamageClaimsSubmittedPage />} />} />
            <Route path="/shuttle-bus-claims" element={<ProtectedRoute moduleNames={SHUTTLE_BUS_PERMISSION_KEYS} element={<Navigate to="/shuttle-bus/submitted-forms" replace />} />} />
            <Route path="/shuttle-bus/open-form" element={<ProtectedRoute moduleNames={SHUTTLE_OPEN_KEYS} element={<ShuttleBusOpenPage />} />} />
            <Route path="/shuttle-bus/submitted-forms" element={<ProtectedRoute moduleNames={SHUTTLE_SUBMITTED_KEYS} element={<ShuttleBusSubmittedPage />} />} />
            <Route path="/shuttle-bus/submitted-forms/:id/edit" element={<ProtectedRoute moduleNames={SHUTTLE_SUBMITTED_KEYS} element={<ShuttleBusEditPage />} />} />
            <Route path="/shuttle-bus/closed-forms" element={<ProtectedRoute moduleNames={SHUTTLE_CLOSED_KEYS} element={<ShuttleBusClosedPage />} />} />
            <Route path="/shuttle-bus/close-form" element={<ProtectedRoute moduleNames={SHUTTLE_CLOSED_KEYS} element={<ShuttleBusClosedPage />} />} />
            <Route path="/closed-shuttle-bus-form" element={<ProtectedRoute moduleNames={SHUTTLE_CLOSED_KEYS} element={<ShuttleBusClosedPage />} />} />
            <Route path="/windshield-tire-claims" element={<ProtectedRoute moduleNames={WINDSHIELD_OPEN_KEYS} element={<Navigate to="/windshield-tire-claims/open-form" replace />} />} />
            <Route path="/windshield-tire-claims/open-form" element={<ProtectedRoute moduleNames={WINDSHIELD_OPEN_KEYS} element={<WindshieldTireOpenPage />} />} />
            <Route path="/windshield-tire-claims/submitted-forms" element={<ProtectedRoute moduleNames={WINDSHIELD_SUBMITTED_KEYS} element={<WindshieldTireSubmittedPage />} />} />
            <Route path="/windshield-tire-claims/submitted-forms/:id/edit" element={<ProtectedRoute moduleNames={WINDSHIELD_SUBMITTED_KEYS} element={<EditSubmittedForm {...WINDSHIELD_TIRE_SUBMITTED_PROPS.editFormProps} />} />} />
            <Route path="/submited-windshield-tire-forms" element={<ProtectedRoute moduleNames={WINDSHIELD_SUBMITTED_KEYS} element={<WindshieldTireSubmittedPage />} />} />
            <Route path="/submited-windshield-tire-forms/:id/edit" element={<ProtectedRoute moduleNames={WINDSHIELD_SUBMITTED_KEYS} element={<EditSubmittedForm {...WINDSHIELD_TIRE_SUBMITTED_PROPS.editFormProps} />} />} />
            <Route path="/closed-windshield-tire-form" element={<ProtectedRoute moduleNames={WINDSHIELD_CLOSED_KEYS} element={<WindshieldTireClosedPage />} />} />
            <Route path="/access" element={<ProtectedRoute moduleNames={['Access Setup', 'Access']} element={<AccessSetupPage />} />} />
            <Route path="/user" element={<ProtectedRoute moduleNames={['User Create', 'User']} element={<UserCreatePage />} />} />
            <Route path="/event-logs" element={<ProtectedRoute moduleNames={['Event Logs', 'Event Log']} element={<EventLogsPage />} />} />
            <Route path="/eventlogs" element={<ProtectedRoute moduleNames={['Event Logs', 'Event Log']} element={<EventLogsPage />} />} />
            <Route path="/event-log" element={<ProtectedRoute moduleNames={['Event Logs', 'Event Log']} element={<EventLogsPage />} />} />
            <Route path="/event_logs" element={<ProtectedRoute moduleNames={['Event Logs', 'Event Log']} element={<EventLogsPage />} />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/pending-claims-reports" element={<ProtectedRoute moduleNames={PENDING_REPORT_KEYS} defaultWhenUnset={false} element={<PendingClaimsReport />} />} />
            <Route path="/closed-claims-reports" element={<ProtectedRoute moduleNames={CLOSED_REPORT_KEYS} defaultWhenUnset={false} element={<ClosedClaimsReport />} />} />
            <Route path="/weekly-claims-ppt" element={<ProtectedRoute moduleNames={WEEKLY_PPT_REPORT_KEYS} defaultWhenUnset={false} element={<WeeklyClaimsPptReport />} />} />
            <Route path="/search-records" element={<ProtectedRoute moduleNames={SEARCH_RECORDS_KEYS} defaultWhenUnset={false} element={<SearchRecordsPage />} />} />
            <Route path="/insights" element={<ProtectedRoute moduleNames={INSIGHTS_KEYS} defaultWhenUnset={false} element={<InsightsPage />} />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
          </>)
        }
      </Routes>
    </BrowserRouter>
  );
}

export default AppRoutes;
